import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { conciergeApi } from "../services/externalPlatformApi";

const GDB_BACKGROUND_VIDEO = "/assets/gdb/gdb-background.mp4";
const DESTINATIONS = ["London", "Ibiza", "Marbella", "Amsterdam", "Malta"];
const INTERESTS = [
  "Groove Dance Beats parties",
  "Pop-up events",
  "After parties",
  "Villa parties",
  "VIP tables",
  "Guestlist access",
  "Hotel packages",
  "Creator/DJ opportunities",
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  instagram: "",
  preferredDestination: "Ibiza",
  interests: ["Groove Dance Beats parties", "After parties"],
  notes: "",
};

export default function GrooveDanceBeatsSignup() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const selectedText = useMemo(() => form.interests.join(", "), [form.interests]);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function toggleInterest(interest) {
    setForm((current) => ({
      ...current,
      interests: current.interests.includes(interest)
        ? current.interests.filter((item) => item !== interest)
        : [...current.interests, interest],
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setStatus("");

    try {
      await conciergeApi.gdbSignup({
        name: form.name,
        email: form.email,
        phone: form.phone,
        destination: `Groove Dance Beats signup - ${form.preferredDestination}`,
        party_size: 1,
        services: form.interests,
        dates: "GDB signup list",
        budget: "Signup / updates",
        special_requests: [
          "SOURCE: Groove Dance Beats standalone signup page",
          `Preferred destination: ${form.preferredDestination}`,
          `Interests: ${selectedText || "Not specified"}`,
          form.instagram ? `Instagram: ${form.instagram}` : "",
          form.notes ? `Notes: ${form.notes}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
      });

      setForm(EMPTY_FORM);
      setStatus("You are on the list. Your Groove Dance Beats preferences have been saved for events, pop-ups, VIP and after-party updates.");
    } catch {
      setStatus("Could not save this yet. Please refresh and try again in a moment.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet>
        <title>Groove Dance Beats Signup | HLT</title>
        <meta
          name="description"
          content="Join the Groove Dance Beats list for parties, pop-up events and after parties across London, Ibiza, Marbella, Amsterdam and Malta."
        />
        <link rel="preload" href={GDB_BACKGROUND_VIDEO} as="video" type="video/mp4" />
      </Helmet>

      <section className="group/gdb relative isolate min-h-screen overflow-hidden border-b border-white/10 px-4 py-5 sm:px-6 lg:px-8">
        <video
          className="absolute inset-0 -z-30 h-full w-full scale-110 object-cover opacity-70 transition duration-700 ease-out group-hover/gdb:scale-[1.14] sm:opacity-75"
          src={GDB_BACKGROUND_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_28%_26%,rgba(212,175,55,0.24),transparent_28%),linear-gradient(90deg,rgba(0,0,0,0.9),rgba(0,0,0,0.62)_42%,rgba(0,0,0,0.86))]" />
        <div className="absolute inset-0 -z-20 bg-[linear-gradient(180deg,rgba(0,0,0,0.62),rgba(0,0,0,0.18)_38%,rgba(0,0,0,0.88))]" />
        <div className="absolute inset-0 -z-10 opacity-[0.16] mix-blend-screen [background-image:linear-gradient(rgba(255,255,255,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:72px_72px]" />

        <div className="relative mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-7xl flex-col">
          <div className="flex items-center justify-between border-b border-white/10 pb-4 backdrop-blur-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.36em] text-brand-500 drop-shadow-[0_0_20px_rgba(212,175,55,0.35)]">Groove Dance Beats</p>
            <Link to="/" className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-300 transition hover:text-white">
              HLT
            </Link>
          </div>

          <div className="grid flex-1 gap-8 py-8 lg:grid-cols-[minmax(0,1fr),25rem] lg:items-center lg:py-10 xl:gap-12">
            <div className="space-y-7 lg:translate-y-[-2vh]">
              <div className="max-w-3xl [transform:perspective(1200px)_rotateX(0deg)_rotateY(-2deg)] transition duration-700 group-hover/gdb:[transform:perspective(1200px)_rotateX(1deg)_rotateY(0deg)_translateZ(18px)]">
                <h1 className="font-display text-5xl leading-[0.9] text-white drop-shadow-[0_18px_45px_rgba(0,0,0,0.7)] sm:text-6xl md:text-7xl xl:text-8xl">
                  Events, pop-ups and <span className="text-brand-500 drop-shadow-[0_0_22px_rgba(212,175,55,0.35)]">after parties</span>
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-200 drop-shadow-[0_10px_30px_rgba(0,0,0,0.65)] sm:text-lg sm:leading-8">
                  First access to Groove Dance Beats nights, secret pop-ups, villa parties, VIP tables and destination moments across London, Ibiza, Marbella, Amsterdam and Malta.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                {DESTINATIONS.map((destination) => (
                  <span key={destination} className="border border-white/15 bg-black/35 px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-100 shadow-[0_18px_50px_rgba(0,0,0,0.35)] backdrop-blur-md sm:px-4">
                    {destination}
                  </span>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a href="#gdb-signup-form" className="btn-primary justify-center shadow-[0_20px_70px_rgba(212,175,55,0.18)]">Get on the list</a>
                <Link to="/events" className="btn-secondary justify-center bg-black/25 backdrop-blur-md">Explore events</Link>
              </div>
            </div>

            <form id="gdb-signup-form" onSubmit={submit} className="border border-white/10 bg-[#080808]/82 p-4 shadow-[0_30px_90px_rgba(0,0,0,0.58)] backdrop-blur-xl sm:p-5 lg:sticky lg:top-6">
              <p className="text-xs uppercase tracking-[0.28em] text-brand-500">Sign up</p>
              <h2 className="mt-2 font-display text-4xl leading-none text-white">GDB access</h2>

              <div className="mt-5 space-y-4">
                <Field label="Name">
                  <input className="input bg-black/45" value={form.name} onChange={(event) => updateField("name", event.target.value)} required />
                </Field>
                <Field label="Email">
                  <input type="email" className="input bg-black/45" value={form.email} onChange={(event) => updateField("email", event.target.value)} required />
                </Field>
                <Field label="Phone / WhatsApp">
                  <input className="input bg-black/45" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} />
                </Field>
                <Field label="Instagram">
                  <input className="input bg-black/45" value={form.instagram} onChange={(event) => updateField("instagram", event.target.value)} placeholder="@yourhandle" />
                </Field>
                <Field label="Main destination">
                  <select className="input bg-black/45" value={form.preferredDestination} onChange={(event) => updateField("preferredDestination", event.target.value)}>
                    {DESTINATIONS.map((destination) => <option key={destination}>{destination}</option>)}
                  </select>
                </Field>

                <div>
                  <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-300">What do you want access to?</div>
                  <div className="grid grid-cols-2 gap-2">
                    {INTERESTS.map((interest) => (
                      <button
                        key={interest}
                        type="button"
                        className={form.interests.includes(interest) ? "btn-primary justify-center px-2 py-2 text-[10px]" : "btn-secondary justify-center bg-black/25 px-2 py-2 text-[10px] backdrop-blur-sm"}
                        onClick={() => toggleInterest(interest)}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                <Field label="Anything specific?">
                  <textarea
                    className="textarea min-h-24 bg-black/45"
                    value={form.notes}
                    onChange={(event) => updateField("notes", event.target.value)}
                    placeholder="Dates, group size, favourite venues, VIP/table requests or destination plans."
                  />
                </Field>

                <button className="btn-primary w-full justify-center" disabled={saving}>
                  {saving ? "Joining..." : "Join the GDB list"}
                </button>

                {status && (
                  <div className="border border-white/10 bg-black/45 p-3 text-sm leading-6 text-slate-200 backdrop-blur-sm">
                    {status}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-300">{label}</span>
      {children}
    </label>
  );
}
